const API_BASE = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5001/api';

function getAuthToken() {
  return localStorage.getItem('e3_auth_token');
}

async function request(url, options = {}) {
  const token = getAuthToken();
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const res = await fetch(`${API_BASE}${url}`, { ...options, headers });
  if (res.status === 401) {
    // Token expired or invalid — clear it
    localStorage.removeItem('e3_auth_token');
    localStorage.removeItem('e3_user');
    window.dispatchEvent(new Event('auth-expired'));
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    const err = new Error(body.error || 'Request failed');
    err.status = res.status;
    err.code = body.code;
    // Preserve the full response body so callers can read structured
    // fields beyond `error`/`code` -- e.g. the CE-sync ordering 409
    // returns blocking_period_id / blocking_period_name so the UI can
    // surface a "Retry the earlier period first" dialog naming the
    // specific row that's blocking.
    err.body = body;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

// Auth
export const getMe = () => request('/auth/me');
export const getUsers = () => request('/auth/users');
export const createUser = (data) => request('/auth/users', { method: 'POST', body: JSON.stringify(data) });
export const updateUser = (id, data) => request(`/auth/users/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteUser = (id) => request(`/auth/users/${id}`, { method: 'DELETE' });
export const getPMList = () => request('/auth/pm-list');
export const getRoles = () => request('/auth/roles');
export const getCapabilities = () => request('/auth/capabilities');
export const getOrgTree = () => request('/auth/org-tree');
export const getSubordinates = (userId) => request(`/auth/subordinates/${userId}`);

// AFP signature routing — who signs and who notarises this pay application.
// Decided per application by whoever is on the accounting list, because the
// two people have to be physically together to notarise.
export const getAfpRouting = (token) => request(`/afp-routing/${encodeURIComponent(token)}`);
export const sendAfpRouting = (token, signer_email, notary_email) =>
  request(`/afp-routing/${encodeURIComponent(token)}/send`, {
    method: 'POST',
    body: JSON.stringify({ signer_email, notary_email }),
  });

// Projects
export const getProjects = (opts = {}) => {
  const params = new URLSearchParams();
  if (opts.status) params.set('status', opts.status);
  if (opts.exclude_status) params.set('exclude_status', opts.exclude_status);
  // The test jobs this person may see (only the Projects dashboard asks; every other list leaves them out).
  if (opts.include_test) params.set('include_test', '1');
  const qs = params.toString();
  return request(`/projects${qs ? '?' + qs : ''}`);
};
export const getProject = (id) => request(`/projects/${id}`);
export const getProjectBySlug = (slug) => request(`/projects/by-slug/${encodeURIComponent(slug)}`);
export const getProjectSummary = (id) => request(`/projects/${id}/summary`);
export const createProject = (data) => request('/projects', { method: 'POST', body: JSON.stringify(data) });
export const createIntakeProject = (data) => request('/projects/intake', { method: 'POST', body: JSON.stringify(data) });
export const assignPM = (id, data) => request(`/projects/${id}/assign-pm`, { method: 'PUT', body: JSON.stringify(data) });
export const updateProject = (id, data) => request(`/projects/${id}`, { method: 'PUT', body: JSON.stringify(data) });
// Job archive (#18): archive keeps everything; delete works only on an archived job.
export const archiveProject = (id) => request(`/projects/${id}/archive`, { method: 'POST' });
export const restoreProject = (id) => request(`/projects/${id}/restore`, { method: 'POST' });
export const uploadProjectLogo = (id, logo_url) => request(`/projects/${id}/logo`, { method: 'PUT', body: JSON.stringify({ logo_url }) });

// SOV Lines
export const getSOVLines = (projectId) => request(`/sov-lines/project/${projectId}`);
export const getSOVLine = (id) => request(`/sov-lines/${id}`);
export const createSOVLine = (data) => request('/sov-lines', { method: 'POST', body: JSON.stringify(data) });
export const bulkCreateSOVLines = (project_id, lines) =>
  request('/sov-lines/bulk', { method: 'POST', body: JSON.stringify({ project_id, lines }) });
export const updateSOVLine = (id, data) => request(`/sov-lines/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteSOVLine = (id) => request(`/sov-lines/${id}`, { method: 'DELETE' });
export const checkSOVAllocation = (projectId) => request(`/sov-lines/project/${projectId}/allocation-check`);
export const getSOVAllocatedTotals = (projectId) => request(`/sov-lines/project/${projectId}/allocated-totals`);
export const rollupSOVLine = (id) => request(`/sov-lines/${id}/rollup`, { method: 'POST' });
export const getRollupSummary = (projectId) => request(`/sov-lines/project/${projectId}/rollup-summary`);
export const resetSOVProgress = (projectId) => request(`/sov-lines/project/${projectId}/reset-progress`, { method: 'POST' });
export const getSOVChangeLog = (id) => request(`/sov-lines/${id}/change-log`);
export const getSOVProjectChangeLog = (projectId) => request(`/sov-lines/project/${projectId}/change-log`);

// Tasks
export const getTasks = (projectId) => request(`/tasks/project/${projectId}`);
export const getTask = (id) => request(`/tasks/${id}`);
export const createTask = (data) => request('/tasks', { method: 'POST', body: JSON.stringify(data) });
export const bulkCreateTasks = (project_id, tasks) =>
  request('/tasks/bulk', { method: 'POST', body: JSON.stringify({ project_id, tasks }) });
export const updateTask = (id, data) => request(`/tasks/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteTask = (id) => request(`/tasks/${id}`, { method: 'DELETE' });
export const getGanttData = (projectId) => request(`/tasks/project/${projectId}/gantt`);
export const addPredecessor = (taskId, data) => request(`/tasks/${taskId}/predecessors`, { method: 'POST', body: JSON.stringify(data) });
export const updatePredecessor = (taskId, predId, data) => request(`/tasks/${taskId}/predecessors/${predId}`, { method: 'PUT', body: JSON.stringify(data) });
export const removePredecessor = (taskId, predId) => request(`/tasks/${taskId}/predecessors/${predId}`, { method: 'DELETE' });
export const getDependencyAlerts = (projectId) => request(`/tasks/project/${projectId}/dependency-alerts`);
// Subtasks (2026-11-01): what a parent still holds that its subtasks should, and moving it down (SplitDown.js).
export const getSplitPreview = (taskId) => request(`/tasks/${taskId}/split-preview`);
export const splitTaskDown = (taskId, data) => request(`/tasks/${taskId}/split`, { method: 'POST', body: JSON.stringify(data) });
// Dynamic scheduling (#50–#52): where a task would land without saving, and the project's work week.
export const previewSchedule = (projectId, data) => request(`/tasks/project/${projectId}/schedule-preview`, { method: 'POST', body: JSON.stringify(data) });
export const setProjectWorkWeek = (projectId, workWeek) => request(`/projects/${projectId}/work-week`, { method: 'PATCH', body: JSON.stringify({ work_week: workWeek }) });
// A test job's test date ('YYYY-MM-DD'), or null for the real date (2026-10-20).
export const setProjectTestDate = (projectId, testToday) => request(`/projects/${projectId}/test-date`, { method: 'PUT', body: JSON.stringify({ test_today: testToday }) });
export const getAllDependencyAlerts = () => request('/tasks/all-dependency-alerts');

// Sections
export const getSections = (projectId) => request(`/sections/project/${projectId}`);
export const createSection = (data) => request('/sections', { method: 'POST', body: JSON.stringify(data) });

// Schedule-tab sections (separate from SOV sections by design — different
// real-world concept: SOV groups costs, Schedule groups work). Same shape
// as the sections API so the TasksTab swap is mechanical.
export const getScheduleSections = (projectId) => request(`/schedule-sections/project/${projectId}`);
export const createScheduleSection = (data) => request('/schedule-sections', { method: 'POST', body: JSON.stringify(data) });
export const deleteScheduleSection = (id) => request(`/schedule-sections/${id}`, { method: 'DELETE' });
export const updateScheduleSection = (id, data) => request(`/schedule-sections/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const reorderScheduleSections = (projectId, order) => request(`/schedule-sections/project/${projectId}/reorder`, { method: 'PUT', body: JSON.stringify({ order }) });
export const updateSection = (id, data) => request(`/sections/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteSection = (id) => request(`/sections/${id}`, { method: 'DELETE' });
export const reorderSections = (projectId, order) => request(`/sections/project/${projectId}/reorder`, { method: 'PUT', body: JSON.stringify({ order }) });

// Allocations
export const getAllocations = (projectId) => request(`/allocations/project/${projectId}`);
export const getSOVAllocations = (sovLineId) => request(`/allocations/sov/${sovLineId}`);
export const getTaskAllocations = (taskId) => request(`/allocations/task/${taskId}`);
export const createAllocation = (data) => request('/allocations', { method: 'POST', body: JSON.stringify(data) });
export const updateAllocation = (id, data) => request(`/allocations/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteAllocation = (id) => request(`/allocations/${id}`, { method: 'DELETE' });

// Billing
export const getBillingPeriods = (projectId) => request(`/billing/project/${projectId}`);
export const getBillingPeriod = (id) => request(`/billing/${id}`);
export const createBillingPeriod = (data) => request('/billing', { method: 'POST', body: JSON.stringify(data) });
export const updateBillingPeriod = (id, data) => request(`/billing/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteBillingPeriod = (id) => request(`/billing/${id}`, { method: 'DELETE' });
export const generateBillingLines = (id) => request(`/billing/${id}/generate-lines`, { method: 'POST' });
export const updateBillingLine = (lineId, data) => request(`/billing/lines/${lineId}`, { method: 'PUT', body: JSON.stringify(data) });
export const approveBilling = (id) => request(`/billing/${id}/approve`, { method: 'POST' });
export const lockBilling = (id) => request(`/billing/${id}/lock`, { method: 'POST' });
export const advanceBillingStatus = (id) => request(`/billing/${id}/advance-status`, { method: 'POST' });
// Retry a CE-Agent sync that failed on the original Finalize (e.g.
// network blip, agent restart). Server runs the same submission
// path without re-running rollup; period stays Finalized either way.
// 409 with err.body.blocking_period_name when an earlier period
// must be synced first; 502 with err.body.period containing the
// fresh DB row when the submission errored.
export const retryBillingCESync = (id) => request(`/billing/${id}/retry-ce-sync`, { method: 'POST' });
// The revision does NOT inherit the original's invoice date -- see the server.
// Pass one to override; omit and the server stamps today (business timezone).
export const createRevisedBillingPeriod = (id, data) =>
  request(`/billing/${id}/create-revised`, { method: 'POST', body: JSON.stringify(data || {}) });
export const getScheduleComparison = (id) => request(`/billing/${id}/schedule-comparison`);
export const getSchedulePerformance = (projectId) => request(`/billing/project/${projectId}/schedule-performance`);
export const getBillingPerformance = (projectId) => request(`/billing/project/${projectId}/billing-performance`);
export const refreshFromSchedule = (id) => request(`/billing/${id}/refresh-from-schedule`, { method: 'POST' });
export const getProgressAlerts = (id) => request(`/billing/${id}/progress-alerts`);
export const getProgressAlertsSummary = (projectId) => request(`/billing/project/${projectId}/progress-alerts-summary`);
export const getAllProjectsAlerts = () => request('/billing/all-projects-alerts');

// Accounts Receivable
export const getAccountsReceivable = () => request('/ar');

// AR mirror (CE actuals synced by CE-Agent — see /api/ar-sync)
export const getArSyncAging = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/ar-sync/aging${qs ? '?' + qs : ''}`);
};
export const getArSyncAgingSnapshots = () => request('/ar-sync/aging/snapshots');
export const getArSyncCollections = (period) =>
  request(`/ar-sync/collections${period ? `?period=${encodeURIComponent(period)}` : ''}`);
export const getArSyncCollectionPeriods = () => request('/ar-sync/collections/periods');
// AR sync settings (scheduler cadence, pull-now, backfill) — Settings → AR Sync panel
export const getArSyncSettings = () => request('/ar-sync/settings');
// runTime: optional { run_hour: 0-23, run_day: 1-7 } (Central); omitted fields keep their saved value.
export const setArSyncCadence = (cadence, runTime) => request('/ar-sync/cadence', { method: 'POST', body: JSON.stringify({ cadence, ...runTime }) });
export const pullArSyncNow = (repull = false) => request('/ar-sync/pull-current', { method: 'POST', body: JSON.stringify({ repull }) });
export const getArSyncBackfill = () => request('/ar-sync/backfill');
export const runArSyncBackfill = (startMonth) => request('/ar-sync/backfill', { method: 'POST', body: JSON.stringify(startMonth ? { start_month: startMonth } : {}) });
// Settings → Invoice Lines: the CE Agent's AIA invoice reads (Billing by Phase) — see /api/aia-reads
// A project's SOV tab, for a job billed in CE: its schedule of values as read from CE's AIA invoices.
export const getCeSov = (projectId) => request(`/ce-sov/project/${projectId}`);
export const getContractBreakdown = (projectId) => request(`/ce-sov/project/${projectId}/breakdown`);
export const getAiaReads = () => request('/aia-reads');
export const setAiaReadCadence = (cadence, runTime) => request('/aia-reads/cadence', { method: 'POST', body: JSON.stringify({ cadence, ...runTime }) });
export const runAiaReadsNow = () => request('/aia-reads/run', { method: 'POST', body: '{}' });
// Every document of every Job Cost roster job (by job, not by date -- 2026-10-18).
export const startAiaBackfill = () => request('/aia-reads/backfill', { method: 'POST', body: JSON.stringify({ scope: 'roster' }) });
export const stopAiaBackfill = () => request('/aia-reads/backfill/stop', { method: 'POST', body: '{}' });
export const retryAiaRead = (jobNumber, invoiceNumber) => request('/aia-reads/retry', { method: 'POST', body: JSON.stringify({ job_number: jobNumber, invoice_number: invoiceNumber }) });
export const setAiaJobCustomer = (jobNumber, customerNumber) => request('/aia-reads/customer', { method: 'PUT', body: JSON.stringify({ job_number: jobNumber, customer_number: customerNumber }) });
export const getArNotes = () => request('/ar-notes');
export const addArNote = (payload) => request('/ar-notes', { method: 'POST', body: JSON.stringify(payload) });

// Schedule Change Log
export const getScheduleChangeLog = (projectId, params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/schedule-log/project/${projectId}${qs ? '?' + qs : ''}`);
};
export const getScheduleChangeSummary = (projectId) => request(`/schedule-log/project/${projectId}/summary`);
export const getTaskHistory = (projectId, taskId) => request(`/schedule-log/project/${projectId}/task/${taskId}/history`);
export const getScheduleLogPMs = () => request('/schedule-log/pms');
export const getPMScheduleSummary = (pmName) => request(`/schedule-log/pm/${encodeURIComponent(pmName)}/summary`);

// Change Orders
export const getChangeOrders = (projectId) => request(`/change-orders/project/${projectId}`);
export const getChangeOrder = (id) => request(`/change-orders/${id}`);
export const createChangeOrder = (data) => request('/change-orders', { method: 'POST', body: JSON.stringify(data) });
export const updateChangeOrder = (id, data) => request(`/change-orders/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteChangeOrder = (id) => request(`/change-orders/${id}`, { method: 'DELETE' });

// Contract Value Log
export const getContractValueLog = (projectId) => request(`/contract-value-log/project/${projectId}`);
export const getContractValueSummary = (projectId) => request(`/contract-value-log/project/${projectId}/summary`);
export const createManualAdjustment = (data) => request('/contract-value-log/manual-adjustment', { method: 'POST', body: JSON.stringify(data) });

// Forecast
export const getForecasts = (projectId) => request(`/forecast/project/${projectId}`);
export const createForecast = (data) => request('/forecast', { method: 'POST', body: JSON.stringify(data) });
export const updateForecast = (id, data) => request(`/forecast/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteForecast = (id) => request(`/forecast/${id}`, { method: 'DELETE' });
export const autoGenerateForecast = (projectId) => request(`/forecast/project/${projectId}/auto-generate`, { method: 'POST' });
export const getForecastDashboard = (projectId) => request(`/forecast/project/${projectId}/dashboard`);
// Portfolio-wide projected monthly billings across all active projects.
export const getBillingsProjection = () => request('/forecast/all-projects/dashboard');

// Retainage Release
export const releaseRetainage = (sovLineId, data) => request(`/sov-lines/${sovLineId}/release-retainage`, { method: 'POST', body: JSON.stringify(data) });
export const unreleaseRetainage = (sovLineId, data) => request(`/sov-lines/${sovLineId}/unrelease-retainage`, { method: 'POST', body: JSON.stringify(data) });

// Workload
export const getWorkloadDashboard = () => request('/workload/dashboard');
export const updatePMCapacity = (pm_name, capacity_cap) => request('/workload/pm-cap', { method: 'PUT', body: JSON.stringify({ pm_name, capacity_cap }) });

// Exports
export const getExportLogs = (projectId) => request(`/exports/project/${projectId}`);
export const generateBillingExport = (data) => request('/exports/billing-export', { method: 'POST', body: JSON.stringify(data) });
export const generateClientSchedule = (data) => request('/exports/client-schedule', { method: 'POST', body: JSON.stringify(data) });

// Audit Log
export const getAuditLog = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/audit-log${qs ? '?' + qs : ''}`);
};
export const getAuditSummary = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/audit-log/summary${qs ? '?' + qs : ''}`);
};

// CE Activity (role-scoped CE Agent job history -- read-only)
export const getCEActivity = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/ce-activity${qs ? '?' + qs : ''}`);
};

// The CE agent flight recorder's screenshots for a failed / retried / partial job: the last
// ~2 minutes of screen per try plus a full-size shot at the moment it failed. VP / Admin only
// (403 for everyone else). Returns { shots: [{ name, try, kind: 'frame'|'mark', index, captured, size }] }.
export const getCEJobScreenshots = (jobId) => request(`/ce-activity/${encodeURIComponent(jobId)}/screenshots`);

// One screenshot as an object URL (the caller revokes it). Like downloadFile, it can't be a
// plain <img src>: the route needs the Authorization header.
export const fetchCEScreenshotUrl = async (jobId, name) => {
  const token = getAuthToken();
  const res = await fetch(`${API_BASE}/ce-activity/${encodeURIComponent(jobId)}/screenshots/${encodeURIComponent(name)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Screenshot unavailable (${res.status})`);
  return URL.createObjectURL(await res.blob());
};

// The agent heartbeat monitor's persisted verdict. A pure read -- it does NOT
// probe the agent, so polling this is free and it answers instantly even while
// the agent is unreachable, which is exactly when it is worth asking.
export const getCEAgentHealth = () => request('/ce-activity/health');

/* ═══════════════════════════════════════════════
   Project documents
   ═══════════════════════════════════════════════ */

export const getProjectDocuments = (projectId) => request(`/documents?project_id=${projectId}`);
export const deleteProjectDocument = (id) => request(`/documents/${id}`, { method: 'DELETE' });

/**
 * Upload a file as RAW BYTES, not JSON and not multipart.
 *
 * The shared `request()` helper sets Content-Type: application/json and
 * stringifies, which would base64-inflate a 40MB drawing past the server's
 * limit and burn memory on both ends. The server takes the body raw and reads
 * metadata off the query string, so this sends the File object directly.
 */
export const uploadProjectDocument = async (projectId, file, opts = {}) => {
  const params = new URLSearchParams({ project_id: String(projectId), filename: file.name });
  if (opts.docType) params.set('doc_type', opts.docType);
  if (opts.title) params.set('title', opts.title);
  if (opts.billingPeriodId) params.set('billing_period_id', String(opts.billingPeriodId));

  const token = getAuthToken();
  const res = await fetch(`${API_BASE}/documents?${params.toString()}`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    const err = new Error(body.detail || body.error || 'Upload failed');
    err.status = res.status;
    throw err;
  }
  return res.json();
};

/**
 * Download a stored file.
 *
 * ⚠️ CANNOT be a plain <a href>. These routes require an Authorization header,
 * and a bare link sends none -- it would 401 and the browser would render the
 * JSON error as a downloaded file, which looks like a corrupt document. So:
 * fetch with the header, wrap the bytes in an object URL, click it, revoke.
 *
 * `filenameHint` only names the saved file; the server also sets
 * Content-Disposition, but that is ignored for object URLs.
 */
/** A stored file's bytes, fetched with the Authorization header (see downloadFile): to save, or to show. */
export const fetchFileBlob = async (path) => {
  const token = getAuthToken();
  const res = await fetch(`${API_BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    const err = new Error(body.detail || body.error || 'Download failed');
    err.status = res.status;
    throw err;
  }
  return res.blob();
};

export const downloadFile = async (path, filenameHint = 'download') => {
  const blob = await fetchFileBlob(path);
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filenameHint;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Revoke on a delay: revoking synchronously can beat the browser's own
    // read of the URL and produce a zero-byte file in some builds.
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  }
};

export const downloadProjectDocument = (doc) =>
  downloadFile(`/documents/${doc.id}/file`, doc.filename || 'document');

/** The GENERATED pay-app PDF, which lives on billing_periods, not in blob. */
export const downloadGeneratedAfpPdf = (billingPeriodId, hint = 'AFP.pdf') =>
  downloadFile(`/billing/${billingPeriodId}/afp-pdf`, hint);

// Applications to work with E3 (Duncan 2026-10-27): the public form at workwith.e3es.com sends its answers (no sign-in),
// then each file with the upload token the answers came back with; Subcontractors → Applications reviews them.
export const submitSubApplication = async (payload, files) => {
  const created = await request('/apply', { method: 'POST', body: JSON.stringify(payload) });
  for (const [kind, file] of Object.entries(files || {})) {
    if (!file) continue;
    const res = await fetch(`${API_BASE}/apply/${created.id}/files/${kind}`, {
      method: 'POST', body: file,
      headers: { 'Content-Type': file.type || 'application/octet-stream', 'X-Upload-Token': created.token, 'X-Filename': encodeURIComponent(file.name) },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `${file.name} didn’t upload. Try again.`);
    }
  }
  return created;
};
export const getSubApplications = () => request('/sub-applications');
export const getSubApplication = (id) => request(`/sub-applications/${id}`);
export const setSubApplicationStatus = (id, status, note) =>
  request(`/sub-applications/${id}/status`, { method: 'PUT', body: JSON.stringify({ status, note }) });
export const fetchSubApplicationFile = (id, kind) => fetchFileBlob(`/sub-applications/${id}/files/${kind}`);
// The agreements the form links (Duncan 2026-10-27: "the spot in the application mgr for the 2 pdfs that can be replaced
// as needed"): which are on file and each one's file (public: the form reads them), and a new copy of one (Sub
// Applications). `v` busts a browser's copy of the old one.
export const getApplyAgreements = () => request('/apply/agreements');
export const applyAgreementUrl = (kind, v) => `${API_BASE}/apply/agreements/${kind}/file${v ? `?v=${encodeURIComponent(v)}` : ''}`;
export const fetchApplyAgreement = (kind) => fetchFileBlob(`/apply/agreements/${kind}/file`);
export const uploadApplyAgreement = async (kind, file) => {
  const token = getAuthToken();
  const res = await fetch(`${API_BASE}/sub-applications/agreements/${kind}`, {
    method: 'POST', body: file,
    headers: { 'Content-Type': file.type || 'application/pdf', 'X-Filename': encodeURIComponent(file.name), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || 'The upload didn’t go through');
  }
  return res.json();
};
export const downloadSubApplicationFile = (id, kind, name) => downloadFile(`/sub-applications/${id}/files/${kind}`, name || 'document');
// Settings → Applications: the end-of-day email about new applications (Duncan 2026-10-28) -- on or off, the Central hour,
// who gets it by role and by name -- and a test of it to the one asking.
export const getApplicationsEmail = () => request('/sub-applications/digest');
export const saveApplicationsEmail = (settings) => request('/sub-applications/digest', { method: 'PUT', body: JSON.stringify(settings) });
export const sendApplicationsEmailTest = () => request('/sub-applications/digest/test', { method: 'POST', body: '{}' });

// Subcontractor Mgmt (server/routes/subcontractors.js)
export const getSubcontractors = () => request('/subcontractors');
// The schedule's Responsible Party pick-list: name, trades, standing, insurance expiry (anyone signed in).
export const getSubcontractorOptions = () => request('/subcontractors/options');
// A project's Subcontractors tab: its subs (from the schedule and CE), their tasks and documents.
export const getProjectSubcontractors = (projectId) => request(`/subcontractors/project/${projectId}`);
// A sub's billing on a job (2026-10-29): its subcontract amount (added with the subcontract on Documents) and its schedule
// of values -- the PM's own lines, each following one or more of the sub's tasks.
const subBilling = (projectId, subId) => `/subcontractors/project/${projectId}/subs/${subId}`;
export const getSubBilling = (projectId, subId) => request(`${subBilling(projectId, subId)}/billing`);
export const setSubContractAmount = (projectId, subId, amountCents) =>
  request(`${subBilling(projectId, subId)}/contract`, { method: 'PUT', body: JSON.stringify({ amount_cents: amountCents }) });
export const addSubSovLine = (projectId, subId, line) =>
  request(`${subBilling(projectId, subId)}/sov-lines`, { method: 'POST', body: JSON.stringify(line) });
export const updateSubSovLine = (projectId, subId, lineId, patch) =>
  request(`${subBilling(projectId, subId)}/sov-lines/${lineId}`, { method: 'PATCH', body: JSON.stringify(patch) });
export const removeSubSovLine = (projectId, subId, lineId) =>
  request(`${subBilling(projectId, subId)}/sov-lines/${lineId}`, { method: 'DELETE' });
// Its sections, like the job's own SOV (2026-10-29).
export const addSubSovSection = (projectId, subId, name) =>
  request(`${subBilling(projectId, subId)}/sov-sections`, { method: 'POST', body: JSON.stringify({ name }) });
export const renameSubSovSection = (projectId, subId, sectionId, name) =>
  request(`${subBilling(projectId, subId)}/sov-sections/${sectionId}`, { method: 'PATCH', body: JSON.stringify({ name }) });
export const removeSubSovSection = (projectId, subId, sectionId) =>
  request(`${subBilling(projectId, subId)}/sov-sections/${sectionId}`, { method: 'DELETE' });
// A task's Sub SOV links (the sub's task window, 2026-10-29: "We start with a task and then link a schedule value to it"): its
// sub, the sub's lines here, and this task's links -- a percent or an amount of a line.
export const getTaskSubSov = (taskId) => request(`/subcontractors/tasks/${taskId}/sub-sov`);
export const addTaskSubSovLink = (taskId, link) =>
  request(`/subcontractors/tasks/${taskId}/sub-sov-links`, { method: 'POST', body: JSON.stringify(link) });
export const removeTaskSubSovLink = (linkId) => request(`/subcontractors/sub-sov-links/${linkId}`, { method: 'DELETE' });
// Several of a project's tasks to one sub at once (the Subcontractors tab's Assign to a sub).
export const assignTasksToSub = (taskIds, subcontractorId) => request('/tasks/assign', { method: 'POST', body: JSON.stringify({ task_ids: taskIds, subcontractor_id: subcontractorId }) });
export const getSubcontractor = (id) => request(`/subcontractors/${id}`);
export const createSubcontractor = (data) => request('/subcontractors', { method: 'POST', body: JSON.stringify(data) });
export const updateSubcontractor = (id, data) => request(`/subcontractors/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
export const setSubcontractorStanding = (id, standing, reason) =>
  request(`/subcontractors/${id}/standing`, { method: 'PUT', body: JSON.stringify({ standing, reason }) });
export const getSubcontractorTaxId = (id) => request(`/subcontractors/${id}/tax-id`);
export const addSubcontractorNote = (id, kind, body) =>
  request(`/subcontractors/${id}/notes`, { method: 'POST', body: JSON.stringify({ kind, body }) });
export const updateSubcontractorDocument = (docId, data) =>
  request(`/subcontractors/documents/${docId}`, { method: 'PATCH', body: JSON.stringify(data) });
export const removeSubcontractorDocument = (docId) => request(`/subcontractors/documents/${docId}`, { method: 'DELETE' });
export const downloadSubcontractorDocument = (doc) =>
  downloadFile(`/subcontractors/documents/${doc.id}/file`, doc.filename || 'document');
// To look at one in Summit (Duncan 2026-10-22: "We need a preview button"): its bytes, not a saved file.
export const fetchSubcontractorDocument = (doc) => fetchFileBlob(`/subcontractors/documents/${doc.id}/file`);
// A task's work (Duncan 2026-10-22): the lines a sub works for it -- the lighting team's fixtures, imported per task
// from its workbook (read in the browser: lib/fixtureWorkbook.js) and fixed in Summit.
export const getTaskWork = (taskId) => request(`/task-work/task/${taskId}`);
export const importTaskWork = (taskId, body) => request(`/task-work/task/${taskId}/import`, { method: 'POST', body: JSON.stringify(body) });
export const addTaskWorkLine = (taskId, line) => request(`/task-work/task/${taskId}/lines`, { method: 'POST', body: JSON.stringify(line) });
export const updateTaskWorkLine = (lineId, fields) => request(`/task-work/lines/${lineId}`, { method: 'PATCH', body: JSON.stringify(fields) });
export const removeTaskWorkLine = (lineId) => request(`/task-work/lines/${lineId}`, { method: 'DELETE' });
export const removeTaskWork = (taskId) => request(`/task-work/task/${taskId}`, { method: 'DELETE' });
export const downloadTaskWorkFile = (file) => downloadFile(`/task-work/files/${file.id}`, file.filename || 'workbook.xlsx');
// A line's work on E3 Subs (2026-10-23): read, and a PM's actions passed on. Each action carries a requestId so a
// retry of the same click changes nothing twice; each answers { ok, line } (the line's new state).
const lineAct = (path, body) => request(`/task-work/lines/${path}`, { method: 'POST', body: JSON.stringify(body) });
export const getTaskWorkProgress = (taskId) => request(`/task-work/task/${taskId}/progress`);
export const getProjectWorkProgress = (projectId) => request(`/task-work/project/${projectId}/progress`);
// A sub's requests and notes on a job, from every line of its tasks there (2026-10-24): { on, items, error? }.
export const getSubRequests = (projectId, subId) => request(`/task-work/project/${projectId}/sub/${subId}/requests`);
export const completeWorkLine = (lineId, body) => lineAct(`${lineId}/complete`, body);
export const uncompleteWorkLine = (lineId, body) => lineAct(`${lineId}/uncomplete`, body);
export const addWorkPhoto = (lineId, body) => lineAct(`${lineId}/photos`, body);
export const removeWorkPhoto = (lineId, photoId, body) => lineAct(`${lineId}/photos/${photoId}/remove`, body);
export const raiseWorkIssue = (lineId, body) => lineAct(`${lineId}/issues`, body);
export const answerWorkIssue = (lineId, issueId, body) => lineAct(`${lineId}/issues/${issueId}/answer`, body);
export const recordSpotCheck = (lineId, body) => lineAct(`${lineId}/spotcheck`, body);
export const verifyPunch = (lineId, findingId, body) => lineAct(`${lineId}/findings/${findingId}/verify`, body);
// The punch list (2026-10-26), kept on E3 Subs: a job's items and its lines to walk ({ on, items, toWalk, tasks, lines,
// error? }), and E3's Add (photos first, each its own call), Verify, Send back ({ note }) and Delete, each with a requestId.
export const getProjectPunch = (projectId) => request(`/task-work/project/${projectId}/punch`);
const punchAct = (taskId, path, body) => request(`/task-work/task/${taskId}/punch${path}`, { method: 'POST', body: JSON.stringify(body) });
export const addPunchItem = (taskId, body) => punchAct(taskId, '', body);
export const addPunchPhoto = (taskId, body) => punchAct(taskId, '/photos', body);
export const verifyPunchItem = (taskId, punchId, body) => punchAct(taskId, `/${encodeURIComponent(punchId)}/verify`, body);
export const sendBackPunchItem = (taskId, punchId, body) => punchAct(taskId, `/${encodeURIComponent(punchId)}/send-back`, body);
export const removePunchItem = (taskId, punchId, body) => punchAct(taskId, `/${encodeURIComponent(punchId)}/remove`, body);
// The line actions (2026-10-24): E3's note on a line ({ body }) and Partly done ({ installed }).
export const addWorkNote = (lineId, body) => lineAct(`${lineId}/notes`, body);
export const setWorkInstalled = (lineId, body) => lineAct(`${lineId}/installed`, body);
// Subs' date moves (2026-10-24): a sub moves its task's dates on E3 Subs; the PM accepts or declines from the
// Schedule tab. Accept = updateTask with the sub's dates (the PM's own edit), then answerMove ACCEPTED.
export const getProjectMoves = (projectId) => request(`/task-work/project/${projectId}/moves`);
export const previewMove = (taskId, moveId) => request(`/task-work/task/${taskId}/move-preview`, { method: 'POST', body: JSON.stringify({ moveId }) });
export const answerMove = (taskId, moveId, body) => request(`/task-work/task/${taskId}/moves/${encodeURIComponent(moveId)}/answer`, { method: 'POST', body: JSON.stringify(body) });
// Subs' progress waiting on the PM (2026-10-28): Accept = updateTask with its % and status (the PM's own edit); Not now =
// dismissNotification of its key.
export const getProgressWaiting = (projectId) => request(`/task-work/project/${projectId}/progress-waiting`);
// A Documents task's documents (2026-10-24), kept on E3 Subs: the list and folders through Summit, and uploads in
// pieces -- start and finish through Summit, each 8 MB piece PUT from this browser straight to E3 Subs with its upload
// token (Summit's sign-in never goes there).
export const getTaskDocuments = (taskId) => request(`/task-work/task/${taskId}/documents`);
export const startTaskDocument = (taskId, body) => request(`/task-work/task/${taskId}/documents/start`, { method: 'POST', body: JSON.stringify(body) });
export const finishTaskDocument = (taskId, documentId) => request(`/task-work/task/${taskId}/documents/${documentId}/finish`, { method: 'POST', body: '{}' });
export const removeTaskDocument = (taskId, documentId) => request(`/task-work/task/${taskId}/documents/${documentId}/remove`, { method: 'POST', body: '{}' });
export const renameTaskDocument = (taskId, documentId, name) => request(`/task-work/task/${taskId}/documents/${documentId}/rename`, { method: 'POST', body: JSON.stringify({ name }) });
export const moveTaskDocument = (taskId, documentId, folderId) => request(`/task-work/task/${taskId}/documents/${documentId}/move`, { method: 'POST', body: JSON.stringify({ folderId }) });
// The documents' progress (2026-10-25): one slider shared with the sub, 0-100 in steps of 5. Never the task's own % done.
export const setTaskDocumentsPercent = (taskId, percent) => request(`/task-work/task/${taskId}/documents/percent`, { method: 'POST', body: JSON.stringify({ percent }) });
export const addTaskFolder = (taskId, name, parentId) => request(`/task-work/task/${taskId}/folders`, { method: 'POST', body: JSON.stringify({ name, parentId: parentId || null }) });
export const renameTaskFolder = (taskId, folderId, name) => request(`/task-work/task/${taskId}/folders/${folderId}/rename`, { method: 'POST', body: JSON.stringify({ name }) });
export const removeTaskFolder = (taskId, folderId) => request(`/task-work/task/${taskId}/folders/${folderId}/remove`, { method: 'POST', body: '{}' });
// A Documents task's recycle bin (2026-10-25): { on, items, canRestore }, and restoring a file or folder from it.
export const getTaskBin = (taskId) => request(`/task-work/task/${taskId}/bin`);
export const restoreFromTaskBin = (taskId, kind, id) => request(`/task-work/task/${taskId}/bin/restore`, { method: 'POST', body: JSON.stringify({ kind, id }) });
/** One piece of an upload, straight to E3 Subs: { status, nextIndex, code, error } (status 0: not reached). */
export async function putDocumentPiece(piecesUrl, index, token, blob, signal) {
  try {
    const res = await fetch(`${piecesUrl}/${index}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/octet-stream', 'X-Upload-Token': token }, body: blob, signal,
    });
    const body = await res.json().catch(() => ({}));
    return { ...body, status: res.status };
  } catch (e) {
    if (e && e.name === 'AbortError') throw e;
    return { status: 0, error: 'E3 Subs couldn’t be reached.' };
  }
}
// The paragraph a sub's insurer puts on a job's COI ({owner}, {job}, {number} filled in on the project tab).
export const getCoiWording = () => request('/subcontractors/coi-wording');
export const saveCoiWording = (wording) => request('/subcontractors/coi-wording', { method: 'PUT', body: JSON.stringify({ wording }) });
/** The raw file as the body, its details in the query (as uploadProjectDocument). */
// Team Activity (Administration, audit.view): who uses Summit and what they do, over 7 / 30 / 90 days.
export const getTeamActivity = (days) => request(`/team-activity?days=${days}`);
export const getTeamActivityPerson = (userId, days) => request(`/team-activity/person/${userId}?days=${days}`);
export const uploadSubcontractorDocument = async (id, file, details = {}, path = `/subcontractors/${id}/documents`, extra = {}, headers = {}) => {
  const params = new URLSearchParams({ filename: file.name, ...extra });
  Object.entries(details).forEach(([k, v]) => { if (v || v === false) params.set(k, v); });
  const token = getAuthToken();
  const res = await fetch(`${API_BASE}${path}?${params.toString()}`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': file.type || 'application/octet-stream',
      ...headers,
    },
    body: file,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    const err = new Error(body.detail || body.error || 'Upload failed');
    err.status = res.status;
    throw err;
  }
  return res.json();
};

/** A sub's document for one job (its certificate of insurance, job site safety plan or subcontract -- with its
    amount_cents): the raw file as the body; what Summit read off a certificate (scanJobCoi) goes with it, to be kept. */
export const uploadJobDocument = (projectId, subcontractorId, file, details = {}, scan = null) =>
  uploadSubcontractorDocument(null, file, details, `/subcontractors/project/${projectId}/documents`, { subcontractor_id: subcontractorId },
    scan ? { 'X-Coi-Scan': encodeURIComponent(JSON.stringify(scan)) } : {});
/** What a certificate of insurance shows for a job: { status: 'read' | 'unreadable' | 'off' | 'failed', found, checks,
    dates }. Read, not kept. */
export const scanJobCoi = async (projectId, file) => {
  const token = getAuthToken();
  const res = await fetch(`${API_BASE}/subcontractors/project/${projectId}/coi-scan`, {
    method: 'POST',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error || 'Couldn’t read the certificate');
  }
  return res.json();
};

// Sync — CE Financial Data
export const triggerSyncJobCost = (data) => request('/sync/job-cost', { method: 'POST', body: JSON.stringify(data) });
export const triggerSyncAPAR = (data) => request('/sync/ap-ar', { method: 'POST', body: JSON.stringify(data) });
export const getSyncJobStatus = (ceJobId) => request(`/sync/job/${ceJobId}`);
export const getSyncHistory = (limit = 20) => request(`/sync/history?limit=${limit}`);
export const getLastSync = () => request('/sync/last-sync');
export const getSyncJobCosts = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/sync/job-costs${qs ? `?${qs}` : ''}`);
};
export const getSyncJobCostSummary = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/sync/job-costs/summary${qs ? `?${qs}` : ''}`);
};

// Cumulative (to-date) job cost data
export const getSyncJobCostsCurrent = (jobId) => request(`/sync/job-costs/current?job_id=${encodeURIComponent(jobId)}`);
// A job's labor hours from its latest pull: [{ phase, category, category_name, hours_budget, hours }].
export const getJobHoursCurrent = (jobId) => request(`/sync/job-hours/current?job_id=${encodeURIComponent(jobId)}`);
// Cumulative labor hours at each month's latest pull: [{ period_year, period_month, hours_budget, hours }].
export const getJobHoursMonthly = (jobId) => request(`/sync/job-hours/monthly/summary?job_id=${encodeURIComponent(jobId)}`);
// What CE invoiced the job each month, by invoice date: [{ period: 'YYYY-MM', invoiced, invoices }].
export const getArInvoicedMonthly = (jobId) => request(`/ar-sync/invoiced/monthly?job_id=${encodeURIComponent(jobId)}`);
export const getSyncJobCostCurrentSummary = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/sync/job-costs/current/summary${qs ? `?${qs}` : ''}`);
};
// Archived jobs' summaries, grouped by archive_year — for the Job Costs "Archived" section.
export const getSyncJobCostArchivedSummary = () => request('/sync/job-costs/archived/summary');

// Monthly breakdown
export const getSyncJobCostsMonthly = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/sync/job-costs/monthly${qs ? `?${qs}` : ''}`);
};
export const getSyncJobCostMonthlySummary = (jobId) => request(`/sync/job-costs/monthly/summary?job_id=${encodeURIComponent(jobId)}`);

// Phase drill-down & period list
export const getSyncJobCostPhases = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/sync/job-costs/phases${qs ? `?${qs}` : ''}`);
};
// Department Status (Finance) — budget variance by CE phase + booked/current GM.
export const getDeptStatus = () => request('/dept-status');
// Every CE invoice for every job (PSR #68): { as_of, departments, jobs: [{ job, name, slug, pm, cm, sales, phases, invoices }] }.
export const getInvoiceHistory = () => request('/invoice-history');
// Each AIA invoice's continuation-sheet lines, net of retainage (Billing by Phase).
export const getInvoiceLines = () => request('/invoice-history/lines');
// Invoice History's PDF report → E3 people (routes/invoiceReportEmail.js).
export const emailInvoiceReport = (payload) => request('/invoice-history/email', { method: 'POST', body: JSON.stringify(payload) });

export const getSyncJobCostPeriods = (jobId) => request(`/sync/job-costs/periods?job_id=${encodeURIComponent(jobId)}`);

export const getSyncAPAR = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/sync/ap-ar${qs ? `?${qs}` : ''}`);
};
export const getSyncARaging = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/sync/ap-ar/aging${qs ? `?${qs}` : ''}`);
};
export const getSyncDashboard = (jobId) => request(`/sync/dashboard/${jobId}`);

// Cost Code Notes
export const getCostCodeNotes = (jobId) => request(`/cost-code-notes?job_id=${encodeURIComponent(jobId)}`);
// One job's CE collections: collected to date and everything CE has invoiced (collected + still open).
export const getArJobCollections = (jobId) => request(`/ar-sync/collections/job-total?job_id=${encodeURIComponent(jobId)}`);
// CE change orders for one job (latest snapshot of the company-wide Q Tool pull).
export const getCeChangeOrders = (jobId) => request(`/sync/change-orders?job_id=${encodeURIComponent(jobId)}`);
// PSR #14: email the report (a PDF the browser built) to E3 people. The server refuses any
// address that isn't @e3es.com and sends it from noreply@e3es.com, replies to the sender.
export const emailPsr = (projectId, payload) => request(`/psr-email/${encodeURIComponent(projectId)}`, { method: 'POST', body: JSON.stringify(payload) });
export const addCostCodeNote = (data) => request('/cost-code-notes', { method: 'POST', body: JSON.stringify(data) });
export const deleteCostCodeNote = (noteId) => request(`/cost-code-notes/${noteId}`, { method: 'DELETE' });

// Notifications
export const getNotifications = () => request('/notifications');
export const getNotificationCount = () => request('/notifications/count');
export const dismissNotification = (data) => request('/notifications/dismiss', { method: 'POST', body: JSON.stringify(data) });

// Job Cost Roster (admin — Settings) — roster of CE job numbers the team
// pulls job cost for. Summit drives the CE-Agent's inception-to-date pull.
export const getJobCostRoster = () => request('/job-cost-roster');
// A P / E / M job's contract (no project page): { contracts: { JOB: { set, change_orders, cos, value } } };
// setting one is admins only (settings.manage); null clears it.
export const getJobCostContracts = () => request('/job-cost-roster/contracts');
export const setJobCostContract = (job_number, contract_value) => request('/job-cost-roster/contract', { method: 'PUT', body: JSON.stringify({ job_number, contract_value }) });
// The Accounting dashboard: jobs that live only on Job Costs (no project page) and their archive / restore.
export const getAccountingCostsOnlyJobs = () => request('/dashboard/accounting/costs-only');
export const archiveCostsOnlyJob = (job) => request(`/dashboard/accounting/jobs/${encodeURIComponent(job)}/archive`, { method: 'POST', body: JSON.stringify({}) });
export const restoreCostsOnlyJob = (job) => request(`/dashboard/accounting/jobs/${encodeURIComponent(job)}/restore`, { method: 'POST', body: JSON.stringify({}) });
export const saveJobCostRosterEntry = (data) => request('/job-cost-roster', { method: 'POST', body: JSON.stringify(data) });
// Archive (keeps data; filed under the job's contract year, #71). Restore is the Dashboard's (restoreProject); nothing is deleted.
export const archiveJobCostRosterEntry = (id) => request(`/job-cost-roster/${id}/archive`, { method: 'POST', body: JSON.stringify({}) });
export const pullJobCostRoster = () => request('/job-cost-roster/pull-all', { method: 'POST' });
export const pullJobCostRosterOne = (id) => request(`/job-cost-roster/${id}/pull`, { method: 'POST' });
// runTime: optional { run_hour: 0-23, run_day: 1-7 } (Central); omitted fields keep their saved value.
export const setJobCostRosterCadence = (cadence, runTime) => request('/job-cost-roster/cadence', { method: 'POST', body: JSON.stringify({ cadence, ...runTime }) });
// Historical backfill — per-job month-by-month import of past job cost.
export const getJobCostBackfill = () => request('/job-cost-roster/backfill');
export const runJobCostBackfill = (job_number, start_month) => request('/job-cost-roster/backfill', { method: 'POST', body: JSON.stringify({ job_number, start_month }) });
export const setJobCostBackfillStart = (job_number, start_month) => request('/job-cost-roster/backfill/start', { method: 'POST', body: JSON.stringify({ job_number, start_month }) });

// Notification Lists (admin)
export const getNotificationLists = () => request('/notifications/lists');
export const createNotificationList = (data) => request('/notifications/lists', { method: 'POST', body: JSON.stringify(data) });
export const updateNotificationList = (id, data) => request(`/notifications/lists/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const getListMembers = (listId) => request(`/notifications/lists/${listId}/members`);
export const addListMember = (listId, data) => request(`/notifications/lists/${listId}/members`, { method: 'POST', body: JSON.stringify(data) });
export const removeListMember = (listId, memberId) => request(`/notifications/lists/${listId}/members/${memberId}`, { method: 'DELETE' });

// Closeout documents — Facility Navigator's closeout library for the job's district, through
// Summit's server (server/routes/closeout.js). FN holds the files; nothing is copied.
const co = (projectId, path = '') => `/closeout/${encodeURIComponent(projectId)}${path}`;
const coDoc = (projectId, docId, path = '') => co(projectId, `/documents/${encodeURIComponent(docId)}${path}`);
export const getCloseout = (projectId) => request(co(projectId));
export const getCloseoutFnProjects = (projectId) => request(co(projectId, '/fn-projects'));
export const linkCloseout = (projectId, fnProjectId) => request(co(projectId, '/link'), { method: 'PUT', body: JSON.stringify({ fnProjectId }) });
export const unlinkCloseout = (projectId) => request(co(projectId, '/link'), { method: 'DELETE', body: '{}' });
export const getCloseoutUploadUrl = (projectId, data) => request(co(projectId, '/upload-url'), { method: 'POST', body: JSON.stringify(data) });
export const commitCloseoutDoc = (projectId, data) => request(co(projectId, '/documents'), { method: 'POST', body: JSON.stringify(data) });
export const renameCloseoutDoc = (projectId, docId, fileName) => request(coDoc(projectId, docId), { method: 'PATCH', body: JSON.stringify({ fileName }) });
export const moveCloseoutDoc = (projectId, docId, folderId) => request(coDoc(projectId, docId, '/folder'), { method: 'PATCH', body: JSON.stringify({ folderId }) });
export const deleteCloseoutDoc = (projectId, docId) => request(coDoc(projectId, docId), { method: 'DELETE', body: '{}' });
// { url, fileName, contentType, sizeBytes } — a short-lived link straight to the file.
export const getCloseoutDocLink = (projectId, docId, kind) => request(coDoc(projectId, docId, `/${kind}`));
export const createCloseoutFolder = (projectId, parentId, name) => request(co(projectId, '/folders'), { method: 'POST', body: JSON.stringify({ parentId, name }) });
export const updateCloseoutFolder = (projectId, folderId, patch) => request(co(projectId, `/folders/${encodeURIComponent(folderId)}`), { method: 'PATCH', body: JSON.stringify(patch) });
export const deleteCloseoutFolder = (projectId, folderId, force) => request(co(projectId, `/folders/${encodeURIComponent(folderId)}${force ? '?force=1' : ''}`), { method: 'DELETE', body: '{}' });
export const getCloseoutZipLink = (projectId) => request(co(projectId, '/zip'));
// The subs' documents, sent to Closeout (2026-10-25): what's on the job's Documents tasks, a send ({ folderId, items,
// again? }; 409 ALREADY_SENT asks first), how it's going, and the files that didn't go, again.
export const getCloseoutFromSubs = (projectId) => request(co(projectId, '/from-subs'));
export const sendToCloseout = (projectId, data) => request(co(projectId, '/from-subs'), { method: 'POST', body: JSON.stringify(data) });
export const getCloseoutSend = (projectId, sendId) => request(co(projectId, `/from-subs/${encodeURIComponent(sendId)}`));
export const sendToCloseoutAgain = (projectId, sendId) => request(co(projectId, `/from-subs/${encodeURIComponent(sendId)}/again`), { method: 'POST', body: '{}' });
